import { useEffect, useState, useRef, useCallback } from "react";
import {  useLocation } from "react-router-dom";
import {
  fetchZonesPOReports,
  fetchZonePartiesPOReports,
  fetchDashboardReports
} from "Service/ApiService";

/* ======================= HELPERS ======================= */
const useDebounce = (value, delay = 600) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};



const ReportsDashboardController = () => {
  

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isFromApp =  localStorage.getItem('isFromApp') === 'true';
  /* ======================= STATE ======================= */
  const [zoneOptions, setZoneOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);

  // Start as "loading" so initial-filters init doesn't run before fetch effects flip these on.
  const [loadingZones, setLoadingZones] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(true);

  const [filters, setFilters] = useState({
    zone: [],
    customer: []
  });

  const [initialFiltersSet, setInitialFiltersSet] = useState(false);
  const debouncedFilters = useDebounce(filters, 600);

  const dropdownsReady = !loadingZones && !loadingCustomers;
  const firstDashboardCallRef = useRef(false);
  /** Ref used so callDashboardAPI can read latest filters without being in deps (avoids extra effect runs) */
  const filtersRef = useRef(filters);
  /** Avoid calling API again when debouncedFilters only "catches up" to initial filters (same payload) */
  const lastRequestedFiltersRef = useRef(null);

  const cloading = loadingZones || loadingCustomers || loadingDashboard;

  /* ======================= HANDLERS ======================= */
  const handleFilterChange = (field) => (value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value || []
    }));
  };


  const loadAllZones = useCallback(async () => {
    try {
      setLoadingZones(true);

      const res = await fetchZonesPOReports(user.userCode, "collection");

      if (res?.status && Array.isArray(res.zones)) {
        setZoneOptions(
          res.zones.map((z, idx) => ({
            label: z.region?.trim() || "(NO REGION)",
            value: `${z.zoneCode}__${idx}`,
            zoneCode: z.zoneCode,
            amount: z.amount
          }))
        );
      } else {
        setZoneOptions([]);
      }
    } catch (err) {
      console.error("Dashboard zone fetch error:", err);
      setZoneOptions([]);
    } finally {
      setLoadingZones(false);
    }
  }, [user.userCode]);

  /* ======================= LOAD ZONES ======================= */
  useEffect(() => {
    loadAllZones();
    localStorage.setItem('SelectedLedgerCustomer', null);
    localStorage.setItem('SelectedDivLedgerCustomer', null);
  }, [loadAllZones]);

  const fetchCustomers = useCallback(async (search = "") => {
    try {
      setLoadingCustomers(true);

      const res = await fetchZonePartiesPOReports(user.userCode, {
        search,
        type: "collection"
      });

      if (res?.status && Array.isArray(res.parties)) {
        setCustomerOptions(
          res.parties.map((p) => ({
            label: `${p.partyName} -  ${p.city} - ${p.partyCode?.trim()}`,
            value: p.partyCode,
            amount: p.amount
          }))
        );
      } else {
        setCustomerOptions([]);
      }
    } catch (err) {
      console.error("Dashboard customer fetch error:", err);
      setCustomerOptions([]);
    } finally {
      setLoadingCustomers(false);
    }
  }, [user.userCode]);

  const loadAllCustomers = useCallback(async () => {
    await fetchCustomers("");
  }, [fetchCustomers]);

  /* ======================= LOAD CUSTOMERS ======================= */
  useEffect(() => {
    loadAllCustomers();
  }, [loadAllCustomers]);

  /* ======================= INITIAL FILTERS (once when dropdowns ready; one setState = one debounced update = one API call) ======================= */
  const initialFiltersDoneRef = useRef(false);
  useEffect(() => {
    if (loadingZones || loadingCustomers || initialFiltersDoneRef.current) return;
    initialFiltersDoneRef.current = true;
    // Initial default: select all options, but don't override if user already changed something.
    // If only one filter is still empty, only initialize that one.
    const nextFilters = {
      zone: filters.zone.length ? filters.zone : zoneOptions,
      customer: filters.customer.length ? filters.customer : customerOptions
    };
    setFilters(nextFilters);
    filtersRef.current = nextFilters;
    setInitialFiltersSet(true);
  }, [
    loadingZones,
    loadingCustomers,
    zoneOptions,
    customerOptions,
    filters.zone.length,
    filters.customer.length
  ]);

  /* ======================= DASHBOARD API (reads filters from ref so callback is stable; effect only re-runs when dropdownsReady/debouncedFilters change) ======================= */
  const callDashboardAPI = useCallback(async () => {
    const f = filtersRef.current;
    try {
      setLoadingDashboard(true);

      const zoneCodes = (f?.zone ?? []).map(z => z.zoneCode).join(",");
      const zoneNames = (f?.zone ?? []).map(z => z.label).join(",");
      const partyCodes = (f?.customer ?? []).map(c => c.value).join(",");

      const res = await fetchDashboardReports(
        user.userCode,
        1,
        zoneCodes,
        partyCodes,
        "",
        "",
        zoneNames
      );

      setDashboardData(res?.status ? res : null);

    } catch (err) {
      console.error("Dashboard API error:", err);
      setDashboardData(null);
    } finally {
      setLoadingDashboard(false);
    }
  }, [user.userCode]);

  /* ======================= DASHBOARD EFFECT: once when ready + initial filters set, then only when debouncedFilters truly change ======================= */
  const getFiltersSignature = (f) =>
    `${(f?.zone ?? []).map(z => z.zoneCode).join(",")}|${(f?.customer ?? []).map(c => c.value).join(",")}`;

  useEffect(() => {
    if (!dropdownsReady || !initialFiltersSet) return;

    // First call: filtersRef already set by initial-filters effect; do not overwrite
    if (!firstDashboardCallRef.current) {
      firstDashboardCallRef.current = true;
      lastRequestedFiltersRef.current = getFiltersSignature(filtersRef.current);
      callDashboardAPI();
      return;
    }

    // Subsequent runs: only call when debouncedFilters payload actually changed (avoids duplicate when debounce catches up to initial)
    const nextSig = getFiltersSignature(debouncedFilters);
    if (lastRequestedFiltersRef.current === nextSig) return;
    lastRequestedFiltersRef.current = nextSig;
    filtersRef.current = debouncedFilters;
    callDashboardAPI();
  }, [dropdownsReady, initialFiltersSet, debouncedFilters, callDashboardAPI]);

  /* ======================= CUSTOMER SEARCH ======================= */
  const handleCustomerSearch = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) return;
    await fetchCustomers(inputValue);
  };

  /* ======================= EXPORT ======================= */
  return {
    cloading,
    loadingDashboard,

    zoneOptions,
    customerOptions,

    filters,
    handleFilterChange,
    handleCustomerSearch,
    loadAllCustomers,
    loadAllZones,
    dashboardData,
    isFromApp
  };
};

export default ReportsDashboardController;
